import type { TreeNodeType } from '@/shared/lib/tree'
import type { DateRange } from 'react-day-picker'

import { Fragment, useState } from 'react'
import { format } from 'date-fns'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { RHFTextArea, RHFTextField } from '@/shared/components/HookForm'
import SearchField from '@/shared/components/SearchField'
import { filterNodesOfTree, TreeCheckbox } from '@/shared/components/TreePicker'
import { Button } from '@/shared/components/ui/button'
import {
	DatePicker,
	DatePickerCalendar,
	DatePickerContent,
	DatePickerInput,
} from '@/shared/components/ui/date-picker'
import { Field, FieldLabel } from '@/shared/components/ui/field'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Switch } from '@/shared/components/ui/switch'
import { useAppStore } from '@/shared/contexts/app'
import { DateFormat, utcToZonedTime } from '@/shared/tools/dates'

export default function PromoForm({
	options,
}: {
	options: Record<string, TreeNodeType[]>
}) {
	const { t } = useTranslation()

	const dateFormat = useAppStore((state) => state.dateFormat)
	const { control } = useFormContext()

	return (
		<>
			<RHFTextField
				className='w-full'
				required
				autoComplete='off'
				placeholder='eg. Summer sale'
				label={t('general_name')}
				name='name'
			/>
			<RHFTextArea
				required
				autoComplete='off'
				placeholder='eg. Summer sale with 50% discount on all products'
				label={t('general_description')}
				name='description'
				rows={6}
			/>
			<FormField
				control={control}
				name='is_active'
				render={({ field }) => (
					<Field orientation='horizontal' className='w-fit'>
						<Switch checked={field.value} onCheckedChange={field.onChange} />
						<FieldLabel htmlFor='2fa'>Is active</FieldLabel>
					</Field>
				)}
			/>
			<FormField
				control={control}
				name='date'
				render={({ field: { value, onChange } }) => (
					<FormItem>
						<FormLabel>{t('general_period')}</FormLabel>
						<FormControl>
							<DatePicker
								required
								mode='range'
								value={
									{
										from: value?.start
											? utcToZonedTime(new Date(value.start), 'UTC')
											: undefined,
										to: value?.end
											? utcToZonedTime(new Date(value.end), 'UTC')
											: undefined,
									} as DateRange
								}
								onValueChange={(d: DateRange) => {
									onChange({
										start: d.from
											? format(d.from, DateFormat.system)
											: undefined,
										end: d.to ? format(d.to, DateFormat.system) : undefined,
									})
								}}
							>
								<DatePickerInput format={dateFormat} />
								<DatePickerContent>
									<DatePickerCalendar captionLayout='dropdown' />
								</DatePickerContent>
							</DatePicker>
						</FormControl>

						<FormMessage />
					</FormItem>
				)}
			/>

			<FormField
				control={control}
				name='pos_ids'
				render={({ field: { value, onChange } }) => (
					<FormItem>
						<FormLabel>
							{t('sales_items', { defaultValue: 'Sales units' })}
						</FormLabel>
						<PopupArraySelectWidget
							value={value}
							onChange={onChange}
							options={options.pos_ids || []}
						/>
						<FormMessage />
					</FormItem>
				)}
			/>
		</>
	)
}

const PopupArraySelectWidget = ({
	value,
	onChange,
	options,
}: {
	value: string[]
	onChange: (value: string[]) => void
	options: TreeNodeType[]
}) => {
	const { t } = useTranslation()

	const title = t('sales_unit')
	const [isOpen, setIsOpen] = useState(false)
	const [searchText, setSearchText] = useState('')
	const [selected, setSelected] = useState(value || [])
	const close = () => setIsOpen(false)
	const handleClose = () => close()
	const handleApply = () => {
		onChange(selected)
		close()
	}
	const filteredList = filterNodesOfTree(options, (item) =>
		item.name.toLowerCase().includes(searchText.toLowerCase())
	)

	return (
		<Fragment>
			<div className='border-base flex items-center justify-between rounded-lg border border-solid bg-secondary px-4 py-3'>
				<p className='text-sm text-muted-foreground'>
					{value?.length
						? t('categories_selected') + ' ' + value?.length
						: t('itmes_not_selected')}
				</p>
				<Button
					onClick={(e) => {
						e.preventDefault()
						setIsOpen(true)
					}}
				>
					{t('general_select')}
				</Button>
			</div>
			<Modal open={isOpen} onOpenChange={setIsOpen}>
				<ModalContent>
					<ModalHeader>
						<ModalTitle>{title}</ModalTitle>
					</ModalHeader>
					<ModalBody className='text-sm'>
						<SearchField
							className='mb-2.5'
							value={searchText}
							onValueChange={setSearchText}
							placeholder={t('general_search')}
						/>
						<div className='h-64 overflow-y-scroll rounded-lg border bg-secondary p-2'>
							<TreeCheckbox
								tree={filteredList}
								value={selected}
								onChange={setSelected}
							/>
						</div>
					</ModalBody>
					<ModalFooter>
						<Button onClick={handleClose}>{t('general_cancel')}</Button>
						<Button variant='primary' onClick={handleApply}>
							{t('general_apply')}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Fragment>
	)
}
